<template>
  <div class="ccris-supplementary">
    <b-card no-body class="custom-card">
      <b-card-header class="py-2 px-3 custom-card-header">
        <b-row align-v="center">
          <b-col cols="6">
            <h3 class="mb-0">CCRIS SUPPLEMENTARY INFORMATION</h3>
          </b-col>
          <b-col cols="6" class="text-right">
            <b-link href="javascript:void(0)" @click="modals.show_ccris_full_view = true" class="full-view-link">
              <b-icon icon="eye-fill" variant="primary"></b-icon>
              Full View
            </b-link>
          </b-col>
        </b-row>  
      </b-card-header>

      <b-card-body class="p-3">
                <!-- Address Information Section -->
        <div class="mb-4" v-if="hasAddressInformation">
          <h5 style="margin-bottom: 10px;">ADDRESS INFORMATION</h5>
          <div class="table-container">
            <table class="directories-table bordered summary-table" style="width: 100%">
              <thead>
                <tr>
                  <th style="width: 60px">No.</th>
                  <th>Address</th>
                  <th style="width: 120px">State</th>
                  <th style="width: 100px">Postcode</th>
                  <th style="width: 120px">Created Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(address, index) in addressInformation" :key="'address-' + index">
                  <td style="text-align: center">{{ address.no || (index + 1 + '.') }}</td>
                  <td class="text-break">{{ safeDisplay(address.long_address || address.address) }}</td>
                  <td style="text-align: center">{{ safeDisplay(address.state) }}</td>
                  <td style="text-align: center">{{ safeDisplay(address.postcode) }}</td>
                  <td style="text-align: center">{{ formatDate(address.created_date) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Contact Information Section -->
        <div class="mb-4" v-if="hasContactInformation">
          <h5 style="margin-bottom: 10px;">CONTACT INFORMATION</h5>
          <div class="table-container">
            <table class="directories-table bordered summary-table" style="width: 100%">
              <thead>
                <tr>
                  <th style="width: 60px">No.</th>
                  <th>Contact Number</th>
                  <th style="width: 120px">Created Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(contact, index) in contactInformation" :key="'contact-' + index">
                  <td style="text-align: center">{{ contact.no || (index + 1 + '.') }}</td>
                  <td>{{ safeDisplay(contact.contact_number) }}</td>
                  <td style="text-align: center">{{ formatDate(contact.created_date) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Occupation Information Section -->
        <div v-if="hasOccupationInformation">
          <h5 style="margin-bottom: 10px;">OCCUPATION INFORMATION</h5>
          <div class="table-container">
            <table class="directories-table bordered summary-table" style="width: 100%">
              <thead>
                <tr>
                  <th style="width: 60px">No.</th>
                  <th>Occupation</th>
                  <th>Employer</th>
                  <th style="width: 100px">From Date</th>
                  <th style="width: 100px">To Date</th>
                  <th style="width: 120px">Created Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(occupation, index) in occupationInformation" :key="'occupation-' + index">
                  <td style="text-align: center">{{ occupation.no || (index + 1 + '.') }}</td>
                  <td>{{ safeDisplay(occupation.occupation) }}</td>
                  <td>{{ safeDisplay(occupation.employer) }}</td>
                  <td style="text-align: center">{{ formatDate(occupation.from_date) }}</td>
                  <td style="text-align: center">{{ formatDate(occupation.to_date) }}</td>
                  <td style="text-align: center">{{ formatDate(occupation.created_date) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- No Data Message -->
        <div v-if="!hasData" class="no-data-section">
          <p class="text-muted mb-0">No Ccris Supplementary data available</p>
        </div>
      </b-card-body>

      <!-- Full View Modal (still useful for larger screens or detailed view) -->
      <CcrisSupplementaryModal
        :ccrisSupplementary="ccrisSupplementary"
        :show_ccris_full_view.sync="modals.show_ccris_full_view"
      />
    </b-card>
  </div>
</template>

<script>
import CcrisSupplementaryModal from '@/views/CustomerProfile/CustomerModals/CcrisSupplementaryModal.vue'

export default {
  name: 'CcrisSupplementary',
  props: {
    ccrisSupplementary: {
      type: Object,
      default: () => ({
        address_information: [],
        contact_information: [],
        occupation_information: []
      })
    }
  },
  components: {
    CcrisSupplementaryModal,
  },
  data() {
    return {
      modals: {
        show_ccris_full_view: false
      }
    }
  },
  computed: {
    // Extract contact information from ccrisSupplementary
    contactInformation() {
      return this.ccrisSupplementary?.contact_information || [];
    },
    // Extract address information from ccrisSupplementary
    addressInformation() {
      return this.ccrisSupplementary?.address_information || [];
    },
    // Extract occupation information from ccrisSupplementary
    occupationInformation() {
      return this.ccrisSupplementary?.occupation_information || [];
    },
    // Check if data exists
    hasContactInformation() {
      return this.contactInformation.length > 0;
    },
    hasAddressInformation() {
      return this.addressInformation.length > 0;
    },
    hasOccupationInformation() {
      return this.occupationInformation.length > 0;
    },
    hasData() {
      return this.hasContactInformation || this.hasAddressInformation || this.hasOccupationInformation;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      
      try {
        // Check if date is already in dd-mm-yyyy format (like "30-03-2010")
        if (typeof dateString === 'string' && dateString.includes('-')) {
          const parts = dateString.split('-');
          
          // If it's in dd-mm-yyyy format, return as is
          if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return dateString;
          }
          
          // If it's in yyyy-mm-dd format, convert to dd-mm-yyyy
          if (parts.length === 3 && parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
          }
        }
        
        // Try to parse as a Date object for other formats
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }).replace(/\//g, '-');
        }
        
        // If all parsing fails, return the original string
        return dateString;
      } catch (e) {
        console.warn('Date parsing error for:', dateString, e);
        return dateString;
      }
    },
    
    // Safe display method to handle empty values
    safeDisplay(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      
      // Handle empty arrays
      if (Array.isArray(value) && value.length === 0) {
        return '-';
      }
      
      // Handle arrays with content
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      
      // Handle empty objects
      if (typeof value === 'object' && Object.keys(value).length === 0) {
        return '-';
      }
      
      // Handle string representations of empty arrays/objects
      if (typeof value === 'string' && (value === '[]' || value === '{}')) {
        return '-';
      }
      
      return value;
    }
  },
};
</script>

<style scoped>
.ccris-supplementary {
  font-family: inherit;
}

.custom-card {
  border: 1px solid #ddd;
  border-radius: 4px;
}

.custom-card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #ddd;
}

.full-view-link {
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
}

.full-view-link:hover {
  text-decoration: underline;
}

/* Styles for the plain HTML tables matching DirectoriesBusinessModal style */
.directories-table {
  border-collapse: collapse;
  width: 100%;
  font-family: inherit;
  font-size: 0.875rem;
}

.directories-table.bordered,
.directories-table th,
.directories-table td {
  border: 1px solid #000 !important;
}

/* Summary table specific header styling */
.summary-table th {
  background-color: #252938 !important;
  color: #fff !important;
  border: none !important;
  vertical-align: top !important;
  text-transform: none !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 19.5px !important;
  padding: 9px 12px !important;
}

.directories-table td {
  background-color: #fff !important;
  color: #000 !important;
  padding: 8px 6px;
  text-align: left;
  font-size: 0.875rem;
}

.directories-table td:first-child {
  text-align: left;
}

.directories-table .text-break {
  white-space: normal;
  word-break: break-word;
  max-width: 400px;
}

/* Section header styling */
h5 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

/* Table container for responsive design */
.table-container {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 1rem;
  max-height: 400px;
  overflow-y: auto;
}

/* Center text for specific cells */
.text-center {
  text-align: center !important;
}

/* No data section */
.no-data-section {
  padding: 20px;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.text-muted {
  color: #6c757d !important;
}

/* Margin adjustments */
.mb-4 {
  margin-bottom: 1.5rem;
}

/* Add scrollbar styling for tables */
.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-container {
    font-size: 0.7rem;
    max-height: 300px;
  }
  
  .directories-table th,
  .directories-table td {
    padding: 6px 4px;
  }
  
  h5 {
    font-size: 14px;
  }
  
  .directories-table .text-break {
    max-width: 200px;
    font-size: 0.7rem;
  }
}

@media (max-width: 576px) {
  .directories-table .text-break {
    max-width: 150px;
  }
}
</style>